import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Bird, Leaf, MapPin, Plus, House, Images, ChartBar,
  WifiSlash, Moon, Sun, Globe, CalendarDots, Bug, Plant, PawPrint, FirstAidKit,
  MagnifyingGlass, SignOut, User, Lock, Eye, EyeSlash, GearSix, List, X
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAppContext } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { useOnlineStatus, getOfflineEntries } from "@/utils/offline";
import { categoryConfig } from "@/config/constants";
import { languageNames } from "@/config/translations";
import { toast } from "sonner";

const Navigation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isOnline = useOnlineStatus();
  const [offlineCount, setOfflineCount] = useState(0);
  const { darkMode, toggleDarkMode, language, setLanguage, t } = useAppContext();
  const { user, logout } = useAuth();
  const [showLangMenu, setShowLangMenu] = useState(false);
  const [showSpeciesMenu, setShowSpeciesMenu] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [pwForm, setPwForm] = useState({ current: "", new: "", confirm: "" });
  const [pwLoading, setPwLoading] = useState(false);
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (pwForm.new.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (pwForm.new !== pwForm.confirm) { toast.error("New passwords don't match"); return; }
    setPwLoading(true);
    try {
      const { API } = await import("@/config/constants");
      await axios.post(`${API}/auth/change-password`, {
        current_password: pwForm.current,
        new_password: pwForm.new
      });
      toast.success("Password changed successfully!");
      setShowChangePassword(false);
      setPwForm({ current: "", new: "", confirm: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change password");
    } finally {
      setPwLoading(false);
    }
  };

  useEffect(() => {
    const checkOfflineEntries = async () => {
      const entries = await getOfflineEntries();
      setOfflineCount(entries.length);
    };
    checkOfflineEntries();

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data.type === 'ENTRY_SYNCED') {
          toast.success(`Synced: ${event.data.title}`);
          checkOfflineEntries();
        }
      });
    }
  }, []);

  useEffect(() => {
    if (!showSpeciesMenu) return;
    const handleClick = () => setShowSpeciesMenu(false);
    const timer = setTimeout(() => document.addEventListener('click', handleClick), 0);
    return () => { clearTimeout(timer); document.removeEventListener('click', handleClick); };
  }, [showSpeciesMenu]);

  const navItemsBefore = [
    { path: "/", icon: House, label: t("journal") },
  ];

  const navItemsAfter = [
    { path: "/calendar", icon: CalendarDots, label: t("calendar") },
    { path: "/gallery", icon: Images, label: t("gallery") },
    { path: "/stats", icon: ChartBar, label: t("stats") },
    { path: "/map", icon: MapPin, label: t("map") },
  ];

  const speciesDropdownItems = [
    { label: "All Species", icon: Leaf, category: null },
    { label: "Birds", icon: Bird, category: "bird" },
    { label: "Plants", icon: Plant, category: "plant" },
    { label: "Wildlife", icon: PawPrint, category: "wildlife" },
    { label: "Insects", icon: Bug, category: "insect" },
    { label: "Fungi", icon: FirstAidKit, category: "fungi" },
  ];

  return (
    <>
    <nav className="sticky top-0 z-50 bg-black/40 backdrop-blur-sm border-b border-white/10 no-print">
      <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14 lg:h-16">
          <div className="flex items-center gap-2 cursor-pointer shrink-0" onClick={() => navigate("/")}>
            <Leaf size={24} weight="duotone" className="text-[#55624F]" />
            <h1 className="font-heading text-lg sm:text-2xl font-semibold text-white hidden sm:block lg:block">{t("appName")}</h1>
            {!isOnline && (
              <Badge className="bg-[var(--accent-ochre)] text-white flex items-center gap-1">
                <WifiSlash size={14} />
                {t("offline")}
              </Badge>
            )}
            {offlineCount > 0 && (
              <Badge className="bg-[var(--accent-terracotta)] text-white">
                {offlineCount} {t("pending")}
              </Badge>
            )}
          </div>

          {/* Mobile/Tablet: just +New and hamburger */}
          <div className="flex items-center gap-1 lg:hidden">
            {user && (
              <Button
                onClick={() => navigate("/entry/new")}
                size="sm"
                className="bg-[var(--accent-terracotta)] text-white hover:opacity-90 h-9 px-2"
                data-testid="new-entry-btn-mobile"
              >
                <Plus size={18} weight="bold" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              className="text-white hover:bg-white/10"
              data-testid="mobile-menu-btn"
            >
              {showMobileMenu ? <X size={22} /> : <List size={22} />}
            </Button>
          </div>

          {/* Desktop: full nav */}
          <div className="hidden lg:flex items-center gap-2">
            {navItemsBefore.map(({ path, icon: Icon, label }) => (
              <Button
                key={path}
                variant="ghost"
                onClick={() => navigate(path)}
                className={`flex items-center gap-2 transition-colors duration-200 ${
                  location.pathname === path
                    ? "bg-[#55624F] text-white hover:bg-[#424D3D]"
                    : "text-white hover:bg-[#55624F]"
                }`}
                data-testid={`nav-${label.toLowerCase()}`}
              >
                <Icon size={20} weight="duotone" />
                <span className="hidden lg:inline">{label}</span>
              </Button>
            ))}

            {/* Species Dropdown */}
            <div className="relative">
              <Button
                variant="ghost"
                onClick={(e) => { e.stopPropagation(); setShowSpeciesMenu(prev => !prev); }}
                className={`flex items-center gap-2 transition-colors duration-200 ${
                  location.pathname === "/species"
                    ? "bg-[#55624F] text-white hover:bg-[#424D3D]"
                    : "text-white hover:bg-[#55624F]"
                }`}
                data-testid="nav-species"
              >
                <Leaf size={20} weight="duotone" />
                <span className="hidden lg:inline">{t("species")}</span>
              </Button>
              {showSpeciesMenu && (
                <div className="absolute left-0 top-full w-48 bg-[var(--bg-card)] border border-[var(--border-default)] rounded-lg shadow-lg z-50 py-1"
                     data-testid="species-dropdown"
                     onClick={(e) => e.stopPropagation()}>
                  {speciesDropdownItems.map(item => {
                    const DropIcon = item.icon;
                    return (
                      <button
                        key={item.label}
                        onClick={() => {
                          navigate(item.category ? `/species?category=${item.category}` : "/species");
                          setShowSpeciesMenu(false);
                        }}
                        className="w-full text-left px-3 py-2 hover:bg-[var(--bg-secondary)] text-sm text-[var(--text-primary)] flex items-center gap-2 transition-colors"
                        data-testid={`species-dropdown-${item.label.toLowerCase().replace(' ', '-')}`}
                      >
                        <DropIcon size={16} weight="duotone" style={{ color: (categoryConfig[item.category] || categoryConfig.unknown).color }} />
                        {item.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {navItemsAfter.map(({ path, icon: Icon, label }) => (
              <Button
                key={path}
                variant="ghost"
                onClick={() => navigate(path)}
                className={`flex items-center gap-2 transition-colors duration-200 ${
                  location.pathname === path
                    ? "bg-[#55624F] text-white hover:bg-[#424D3D]"
                    : "text-white hover:bg-[#55624F]"
                }`}
                data-testid={`nav-${label.toLowerCase()}`}
              >
                <Icon size={20} weight="duotone" />
                <span className="hidden lg:inline">{label}</span>
              </Button>
            ))}

            {/* Dark Mode Toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleDarkMode}
              className="text-[var(--text-secondary)] hover:bg-[#55624F] hover:text-white transition-all duration-200"
              data-testid="dark-mode-toggle"
              title={darkMode ? t("lightMode") : t("darkMode")}
            >
              {darkMode ? <Sun size={20} weight="duotone" /> : <Moon size={20} weight="duotone" />}
            </Button>

            {/* Language Selector */}
            <div className="relative">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setShowLangMenu(!showLangMenu)}
                className="text-white hover:bg-[#55624F] transition-colors duration-200"
                data-testid="language-toggle"
                title={t("language")}
              >
                <Globe size={20} weight="duotone" />
              </Button>
              {showLangMenu && (
                <div className="absolute right-0 mt-2 w-36 bg-[var(--bg-card)] border border-[var(--border-default)] rounded-lg shadow-lg z-50">
                  {Object.entries(languageNames).map(([code, name]) => (
                    <button
                      key={code}
                      onClick={() => {
                        setLanguage(code);
                        setShowLangMenu(false);
                      }}
                      className={`w-full text-left px-4 py-2 hover:bg-[var(--bg-secondary)] text-sm ${
                        language === code ? 'font-semibold text-[var(--brand-primary)]' : 'text-[var(--text-primary)]'
                      } first:rounded-t-lg last:rounded-b-lg`}
                    >
                      {name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Account Settings - only for logged in users */}
            {user && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate("/account")}
                className={`transition-colors duration-200 ${location.pathname === "/account"
                  ? "bg-[#55624F] text-white hover:bg-[#424D3D]"
                  : "text-white hover:bg-[#55624F]"
                }`}
                data-testid="nav-account"
                title="Account Settings"
              >
                <GearSix size={20} weight="duotone" />
              </Button>
            )}

            {/* Sign In button for visitors */}
            {!user && (
              <Button
                onClick={() => navigate("/login")}
                size="sm"
                className="bg-[#55624F] text-white hover:bg-[#424D3D] text-xs"
                data-testid="nav-signin-btn"
              >
                Sign In
              </Button>
            )}

            {user && (
              <Button
                onClick={() => navigate("/entry/new")}
                className="bg-[var(--accent-terracotta)] text-white hover:opacity-90 flex items-center gap-2"
                data-testid="new-entry-btn"
              >
                <Plus size={20} weight="bold" />
                <span className="hidden lg:inline">{t("newEntry")}</span>
              </Button>
            )}

            {/* User Menu */}
            {user && (
              <div className="relative">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="w-9 h-9 rounded-full overflow-hidden border-2 border-[var(--border-default)] hover:border-[var(--brand-primary)] transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)]"
                  data-testid="user-menu-btn"
                  title={user.name || user.email}
                >
                  <img
                    src="https://customer-assets.emergentagent.com/wingman/08b2a83a-b1a7-4498-931c-bef9fe86432a/attachments/32f1d3e2eeb941c2af924c6b1e172cfd_Firefly_GeminiFlash_cartoon eyes open and with a smile and no background_ 167363.png"
                    alt={user.name || "Profile"}
                    className="w-full h-full object-cover"
                  />
                </button>
                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-52 bg-[var(--bg-card)] border border-[var(--border-default)] rounded-lg shadow-lg z-50 py-2" data-testid="user-menu-dropdown">
                    <div className="px-4 py-2 border-b border-[var(--border-default)]">
                      <p className="text-sm font-semibold text-[var(--text-primary)] truncate">{user.name}</p>
                      <p className="text-xs text-[var(--text-secondary)] truncate">{user.email}</p>
                    </div>
                    {!user.has_paid && (
                      <button
                        onClick={() => { navigate("/pricing"); setShowUserMenu(false); }}
                        className="w-full text-left px-4 py-2 text-sm text-[var(--accent-terracotta)] hover:bg-[var(--bg-secondary)] font-medium"
                        data-testid="upgrade-btn"
                      >
                        Upgrade to Lifetime
                      </button>
                    )}
                    <button
                      onClick={async () => { await logout(); navigate("/welcome"); setShowUserMenu(false); }}
                      className="w-full text-left px-4 py-2 text-sm text-[var(--text-secondary)] hover:bg-[var(--bg-secondary)] flex items-center gap-2"
                      data-testid="logout-btn"
                    >
                      <SignOut size={16} /> Sign Out
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {showMobileMenu && (
        <div className="lg:hidden border-t border-white/10 bg-black/80 backdrop-blur-md px-4 py-3 space-y-1" data-testid="mobile-menu">
          {[
            { path: "/", icon: House, label: t("journal") },
            { path: "/species", icon: Leaf, label: t("species") },
            { path: "/calendar", icon: CalendarDots, label: t("calendar") },
            { path: "/gallery", icon: Images, label: t("gallery") },
            { path: "/stats", icon: ChartBar, label: t("stats") },
            { path: "/map", icon: MapPin, label: t("map") },
            { path: "/account", icon: GearSix, label: "Settings" },
          ].map(({ path, icon: Icon, label }) => (
            <button
              key={path}
              onClick={() => { navigate(path); setShowMobileMenu(false); }}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-['Manrope'] transition-colors ${
                location.pathname === path
                  ? "bg-[#55624F] text-white"
                  : "text-white hover:bg-white/10"
              }`}
            >
              <Icon size={18} weight="duotone" /> {label}
            </button>
          ))}
          <div className="flex items-center gap-2 px-3 py-2">
            <Button variant="ghost" size="icon" onClick={() => { toggleDarkMode(); }} className="text-white hover:bg-white/10">
              {darkMode ? <Sun size={18} /> : <Moon size={18} />}
            </Button>
            <Button variant="ghost" size="icon" onClick={() => setShowLangMenu(!showLangMenu)} className="text-white hover:bg-white/10">
              <Globe size={18} />
            </Button>
          </div>
          {user && (
            <div className="border-t border-white/20 pt-2 mt-2">
              <div className="px-3 py-1">
                <p className="text-sm font-semibold text-white truncate">{user.name}</p>
                <p className="text-xs text-white/60 truncate">{user.email}</p>
              </div>
              <button
                onClick={async () => { await logout(); navigate("/welcome"); setShowMobileMenu(false); }}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-white hover:bg-white/10 font-['Manrope']"
              >
                <SignOut size={18} /> Sign Out
              </button>
            </div>
          )}
        </div>
      )}
    </nav>

    <Dialog open={showChangePassword} onOpenChange={setShowChangePassword}>
      <DialogContent className="bg-[var(--bg-card)] border-[var(--border-default)] max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-['Cormorant_Garamond'] text-xl text-[var(--text-primary)]">Change Password</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleChangePassword} className="space-y-4 mt-2">
          <div>
            <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1 font-['Manrope']">Current Password</label>
            <div className="relative">
              <Input
                type={showCurrentPw ? "text" : "password"}
                value={pwForm.current}
                onChange={(e) => setPwForm(p => ({ ...p, current: e.target.value }))}
                className="bg-[var(--bg-secondary)] border-[var(--border-default)] text-[var(--text-primary)] pr-10"
                data-testid="current-password-input"
                required
              />
              <button type="button" onClick={() => setShowCurrentPw(!showCurrentPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]">
                {showCurrentPw ? <EyeSlash size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1 font-['Manrope']">New Password</label>
            <div className="relative">
              <Input
                type={showNewPw ? "text" : "password"}
                value={pwForm.new}
                onChange={(e) => setPwForm(p => ({ ...p, new: e.target.value }))}
                className="bg-[var(--bg-secondary)] border-[var(--border-default)] text-[var(--text-primary)] pr-10"
                data-testid="new-password-input"
                required
                minLength={8}
              />
              <button type="button" onClick={() => setShowNewPw(!showNewPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]">
                {showNewPw ? <EyeSlash size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1 font-['Manrope']">Confirm New Password</label>
            <Input
              type="password"
              value={pwForm.confirm}
              onChange={(e) => setPwForm(p => ({ ...p, confirm: e.target.value }))}
              className="bg-[var(--bg-secondary)] border-[var(--border-default)] text-[var(--text-primary)]"
              data-testid="confirm-password-input"
              required
              minLength={8}
            />
          </div>
          <div className="flex gap-3 pt-2">
            <Button type="button" variant="outline" onClick={() => setShowChangePassword(false)} className="flex-1 border-[var(--border-default)] text-[var(--text-secondary)]">
              Cancel
            </Button>
            <Button type="submit" disabled={pwLoading} className="flex-1 bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white" data-testid="submit-change-password-btn">
              {pwLoading ? "Saving..." : "Change Password"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default Navigation;
